import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import CategoriesSlider from './components/CategoriesSlider';
import ProductSection from './components/ProductSection';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import CookieBanner from './components/CookieBanner';
import { fetchProducts, fetchCart, addToCartApi, preloadedProducts } from './lib/api';

const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Sobre = lazy(() => import('./pages/Sobre'));
const Contato = lazy(() => import('./pages/Contato'));
const Termos = lazy(() => import('./pages/Termos'));
const Privacidade = lazy(() => import('./pages/Privacidade'));
const Trocas = lazy(() => import('./pages/Trocas'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderStatus = lazy(() => import('./pages/OrderStatus'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const Search = lazy(() => import('./pages/Search'));
const Category = lazy(() => import('./pages/Category'));
const Rastreio = lazy(() => import('./pages/Rastreio'));
const NotFound = lazy(() => import('./pages/NotFound'));
import useDocumentMeta from './hooks/useDocumentMeta';
import AnalyticsTracker from './components/AnalyticsTracker';
import { toast } from './hooks/use-toast';
import { Toaster } from './components/ui/sonner';

const SITE_ORIGIN = process.env.REACT_APP_SITE_ORIGIN || 'https://eletrovex.com.br';
const HOME_DESCRIPTION = 'Eletrovex: especialista em exaustores, grades de ventilação, dutos e acessórios para banheiro, cozinha e churrasqueira. Qualidade certificada e envio para todo o Brasil.';

const Home = () => {
  const [products, setProducts] = useState(() => preloadedProducts() || []);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(() => !preloadedProducts());

  useDocumentMeta({
    title: 'Eletrovex | Exaustores, Ventilação e Acessórios',
    description: HOME_DESCRIPTION,
    canonical: `${SITE_ORIGIN}/`,
    image: `${SITE_ORIGIN}/images/itc_fan_branco/foto_1.png`,
    type: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Eletrovex',
      alternateName: 'ELETROVEX',
      url: `${SITE_ORIGIN}/`,
      inLanguage: 'pt-BR',
      publisher: { '@type': 'Organization', name: 'Eletrovex', url: `${SITE_ORIGIN}/` },
      potentialAction: {
        '@type': 'SearchAction',
        target: { '@type': 'EntryPoint', urlTemplate: `${SITE_ORIGIN}/busca?q={search_term_string}` },
        'query-input': 'required name=search_term_string',
      },
    },
  });

  useEffect(() => {
    let mounted = true;
    Promise.all([fetchProducts(), fetchCart()])
      .then(([products, cart]) => {
        if (!mounted) return;
        setProducts(products);
        setCart(cart);
      })
      .catch((err) => console.error('Erro ao carregar dados:', err))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const handleAddToCart = async (product, quantity = 1) => {
    try {
      const item = await addToCartApi({
        product_id: product.id,
        name: product.name,
        image: product.image,
        price: product.price,
        quantity,
        selectedColor: product.color || product.defaultColor || null,
        selectedSize: product.size || product.defaultSize || null,
        selectedVoltage: product.voltage || product.defaultVoltage || null,
        selectedPrimary: product.defaultPrimary || null
      });
      // Update local state
      const existing = cart.find((c) => c.id === item.id);
      const newCart = existing
        ? cart.map((c) => (c.id === item.id ? item : c))
        : [...cart, item];
      setCart(newCart);
      toast('Produto adicionado ao carrinho!', {
        description: `${quantity}x ${product.name}`,
        duration: 2000,
      });
    } catch (err) {
      console.error(err);
      toast('Erro ao adicionar ao carrinho', { duration: 2000 });
    }
  };

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Filter products by category
  const byCat = (cat) => products.filter((p) => p.category === cat);
  const itcFanProducts = byCat('exaustores');
  const kitProducts = byCat('kits');
  const churrasqueiraProducts = byCat('churrasqueira');
  const cooktopsProducts = byCat('cooktops');
  const pecasFogaoProducts = byCat('pecas');

  return (
    <div className="min-h-screen bg-gray-50" data-testid="home-page">
      <h1 className="sr-only" data-testid="home-seo-h1">Eletrovex — Exaustores, Ventilação e Acessórios</h1>
      <Header cartCount={totalItems} />
      <Hero />
      <CategoriesSlider />

      {loading ? (
        <div className="text-center py-16 text-gray-500" data-testid="loading-indicator">
          Carregando produtos...
        </div>
      ) : (
        <>
          <div id="exaustores">
            <ProductSection
              title="ITC FAN 100mm"
              subtitle="Exaustores disponíveis em Branco e Preto - 127v e 220v"
              products={itcFanProducts}
              onAddToCart={handleAddToCart}
            />
          </div>

          <div id="kits" className="bg-gradient-to-br from-gray-100 to-gray-50">
            <ProductSection
              title="KITS COMPLETOS"
              subtitle="Kit completo com exaustor, duto, grelha e todos os acessórios"
              products={kitProducts}
              onAddToCart={handleAddToCart}
            />
          </div>

          <div id="churrasqueira">
            <ProductSection
              title="EXAUSTORES PARA CHURRASQUEIRAS"
              subtitle="Exaustores com iluminação para churrasqueiras - 15cm e 20cm"
              products={churrasqueiraProducts}
              onAddToCart={handleAddToCart}
            />
          </div>

          <div id="cooktops">
            <ProductSection
              title="COOKTOPS"
              subtitle="Cooktops elétricos para fogão de embutir"
              products={cooktopsProducts}
              onAddToCart={handleAddToCart}
            />
          </div>

          <div id="pecas" className="bg-gradient-to-br from-gray-100 to-gray-50">
            <ProductSection
              title="PEÇAS DE FOGÃO"
              subtitle="Usinas centelhadores e kits de registros para fogões cooktop"
              products={pecasFogaoProducts}
              onAddToCart={handleAddToCart}
            />
          </div>
        </>
      )}

      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<div className="min-h-screen bg-gray-50" />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/produto/:id" element={<ProductDetail />} />
          <Route path="/carrinho" element={<Cart />} />
          <Route path="/sobre" element={<Sobre />} />
          <Route path="/contato" element={<Contato />} />
          <Route path="/termos" element={<Termos />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/trocas" element={<Trocas />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/pedido/sucesso" element={<OrderStatus kind="sucesso" />} />
          <Route path="/pedido/falha" element={<OrderStatus kind="falha" />} />
          <Route path="/pedido/pendente" element={<OrderStatus kind="pendente" />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/busca" element={<Search />} />
          <Route path="/categoria/:slug" element={<Category />} />
          <Route path="/rastreio" element={<Rastreio />} />
          <Route path="/produtos" element={<Home />} />
          <Route path="/categorias" element={<Home />} />
          <Route path="/promocoes" element={<Home />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <AnalyticsTracker />
        <FloatingWhatsApp />
        <CookieBanner />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
